import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { DialogContentText } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import { ActionButtonConfirmStatus } from "components";
import { AppDispatch } from "store";
import { deleteTable } from "store/reducers/products/tables/tables.actions";
import { ProcessState } from "types/process";

export interface DeleteRuleTableButtonProps {
  tableId: string;
  tableType?: string;
}

export function DeleteRuleTableButton({
  tableId,
  tableType,
}: DeleteRuleTableButtonProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState("");

  const resetDialogState = useCallback(() => {
    setProcessState(ProcessState.IDLE);
    setStatusMessage("");
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      setProcessState(ProcessState.PROCESSING);
      setStatusMessage("Deleting table...");

      await dispatch(deleteTable(tableId));

      const msg = "Table successfully deleted.";
      toast.success(msg);
      setProcessState(ProcessState.COMPLETE);
      setStatusMessage(msg);
    } catch (error) {
      const msg = `Error deleting table: ${error}`;
      console.error(msg);
      toast.error(msg);
      setProcessState(ProcessState.ERROR);
      setStatusMessage(msg);
    }
  }, [dispatch, tableId]);

  const contentSection = useMemo<ReactNode>(() => {
    const tableLabel = tableType
      ? `${tableType} table ${tableId}`
      : `table ${tableId}`;

    return (
      <DialogContentText>
        Are you sure you want to delete {tableLabel}?
      </DialogContentText>
    );
  }, [tableId, tableType]);

  return (
    <Wrapper>
      <ActionButtonConfirmStatus
        // Button Props
        color="warning"
        size="medium"
        ariaLabel="Delete Table"
        // Icon Props
        icon={DeleteOutlineIcon}
        iconOnly
        // Tooltip Props
        tooltipText="Delete Table"
        tooltipArrow
        tooltipPlacement="bottom"
        // Dialog Props
        maxWidth="sm"
        dialogTitle="Delete Table"
        forceOnTop
        onDialogOpen={resetDialogState}
        disableEscapeKeyDown
        disableCloseOnBackdropClick
        // Content Section Props
        contentSection={contentSection}
        hideContentOnProcessComplete
        // Action Button Props
        defaultButton="CANCEL"
        cancelButtonText="Cancel"
        okButtonText="OK"
        okButtonWarningColor
        onOKButtonClick={handleDelete}
        // Status Section Props
        processState={processState}
        statusMessage={statusMessage}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-left: 30px;
`;

export default DeleteRuleTableButton;
